<template>
  <div class="register">
    <div class="register-title">{{ $t("login.SignUp") }}</div>
    <div class="line" />
    <el-form
      :model="registerForm"
      :rules="registerFormRules"
      ref="registerFormRef"
      class="register-main"
    >
      <el-form-item prop="phone">
        <el-input
          v-model="registerForm.phone"
          :placeholder="$t('login.PleaseEnterMobile')"
          size="large"
        >
          <el-select
            v-model="registerForm.areaNumber"
            slot="prepend"
            placeholder="请选择"
          >
            <img
              slot="prefix"
              src="~@/assets/img/phoneicon.png"
              alt=""
              class="input-icon"
            />
            <el-option
              v-for="item in phoneAreaCode"
              :key="item.id"
              :label="item.name"
              :value="item.name"
            ></el-option>
          </el-select>
        </el-input>
      </el-form-item>
      <el-form-item prop="code">
        <el-input
          v-model="registerForm.code"
          :placeholder="$t('login.PleaseEnterVCode')"
          size="large"
          class="input-margin input-code"
        >
          <img
            src="~@/assets/img/psicon.png"
            alt=""
            slot="prefix"
            class="input-icon"
          />
          <span
            slot="suffix"
            :class="!isGetting ? 'get-code' : 'in-get-code'"
            @click="!isGetting && handleGetCode()"
          >
            {{ codeText }}
          </span>
        </el-input>
      </el-form-item>
      <el-form-item prop="password">
        <el-input
          v-model="registerForm.password"
          :placeholder="$t('login.PleaseEnterPasswordTipCondition')"
          size="large"
          class="input-margin"
          type="password"
        >
          <img
            src="~@/assets/img/psandreps.png"
            alt=""
            slot="prefix"
            class="input-icon"
          />
        </el-input>
      </el-form-item>
      <el-form-item prop="passwordConfirm">
        <el-input
          v-model="registerForm.passwordConfirm"
          :placeholder="$t('login.PleaseEnterPasswordConfirm')"
          size="large"
          class="input-margin"
          type="password"
        >
          <img
            src="~@/assets/img/psandreps.png"
            alt=""
            slot="prefix"
            class="input-icon"
          />
        </el-input>
      </el-form-item>
    </el-form>
    <div class="register-agree">
      <div>
        <el-checkbox v-model="isAgree"
          >{{ $t("login.ContinueAgree") }}
        </el-checkbox>
        <a
          class="login-tips_color"
          style="margin-left: 5px"
          @click.prevent="$emit('handleShowPrivacy')"
          href="#"
          >{{ $t("common.PrivacyPolicy") }}
        </a>
      </div>
    </div>
    <div class="have-account">
      <div>
        {{ $t("login.HaveAccount")
        }}<span
          class="font-color click-cursor"
          @click="$emit('handleGoLogin')"
          >{{ $t("login.AccountLogin") }}</span
        >
      </div>
    </div>
    <button class="login-btn" @click="handleRegister">
      {{ $t("login.SignUp") }}
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, ref } from "vue";
import { checkSmsCode, createUser, sentEmailOrPhoneCode } from "@/api/login";
import phoneAreaCode from "@/utils/areaCode";
import { Message } from "element-ui";
import { checkPhoneExist } from "@/api/user";
import useVerificationCode from "@/hooks/useVerificationCode";
import { useI18n } from "vue-i18n-composable";

const checkRightPhone = (phone) => {
  return /^1[123456789]\d{9}$/.test(phone);
};

const checkRightPassword = (password) => {
  return /^(?![0-9]+$)(?![a-z]+$)(?![A-Z]+$)(?!([^(0-9a-zA-Z)])+$).{8}$/.test(
    password
  );
};

export default defineComponent({
  name: "RegisterForm",
  setup: (_props, { emit }) => {
    const { t } = useI18n();

    const registerForm = reactive({
      phone: "",
      code: "",
      password: "",
      passwordConfirm: "",
      areaNumber: "+86",
    });

    const { getVerificationCode, isGetting, codeText } =
      useVerificationCode(sentEmailOrPhoneCode);

    const isAgree = ref(true);

    const registerFormRef = ref();

    const password = (_rule, value, callback) => {
      if (!checkRightPassword(value)) {
        callback(new Error(<string>t("login.PleaseEnterPasswordTipCondition")));
      } else {
        callback();
      }
    };

    const passwordConfirm = (_rule, value, callback) => {
      if (value !== registerForm.password) {
        callback(new Error(<string>t("login.ConsistentPasswordTip")));
      } else {
        callback();
      }
    };

    const registerFormRules = {
      phone: [
        { required: true, message: t('login.TheMobilePhoneNumberFormatIsIncorrect'), trigger: "change" },
      ],
      code: [
        { required: true, message: t("login.PleaseEnterTheCorrectVcode"), trigger: "change" },
      ],
      password: [{ validator: password, trigger: "change" }],
      passwordConfirm: [{ validator: passwordConfirm, trigger: "change" }],
    };

    const handleGetCode = async () => {
      const { phone } = registerForm;

      const CHINA_AREA_CODE = "+86";
      if (
        registerForm.areaNumber === CHINA_AREA_CODE &&
        !checkRightPhone(phone)
      ) {
        return Message.error(t("login.TheMobilePhoneNumberFormatIsIncorrect") as string);
      }

      const { exist } = await checkPhoneExist(
        registerForm.phone,
        registerForm.areaNumber
      );
      if (exist) return Message.warning(t('login.heMobilePhoneNumberAlreadyExistsPleaseLogInDirectly') as string);

      getVerificationCode(phone, registerForm.areaNumber);
    };

    const handleRegister = () => {
      registerFormRef.value.validate(async (valid) => {
        if (valid) {
          const { phone, code, passwordConfirm } = registerForm;
          if (!isAgree.value) {
            return Message.error("请同意《签泰隐私协议》");
          }
          const { success } = await checkSmsCode(
            phone,
            code,
            registerForm.areaNumber
          );
          if (success) {
            // 验证码正确 创建用户
            const { isSuccess } = await createUser({
                phone,
                password: passwordConfirm,
                areaCode: registerForm.areaNumber
            });
            if (isSuccess) {
              Message.success("注册成功");
              emit("handleGoLogin");
            } else if (!isSuccess) {
              Message.error("该手机号已注册");
            }
          }
        }
      });
    };

    const handleClickMenu = (command) => {
      registerForm.areaNumber = command;
    };

    return {
      registerForm,
      registerFormRef,
      registerFormRules,
      isAgree,
      handleRegister,
      handleClickMenu,
      phoneAreaCode,
      isGetting,
      codeText,
      handleGetCode,
    };
  },
});
</script>

<style scoped lang="scss">
@import "../style/LoginForm";
.register {
  width: 100%;
  @include flex-box(flex-start, center, column);

  &-title {
    color: $color;
    font-size: 18px;
    font-family: $font-weight-5;
  }

  .line {
    margin-top: 13px;
    width: 118px;
    height: 1px;
    border: 1px solid $color;
  }

  &-main {
    width: 100%;
    margin-top: 29px;
    margin-bottom: 20px;
  }

  &-phone {
    font-size: 14px;
    font-family: DINAlternate-Bold, DINAlternate;
    font-weight: bold;
    color: #666666;
    @include flex-box(flex-start, center);

    &_divider {
      margin-left: 5px;
      width: 1px;
      height: 30px;
      border: 1px solid #cccccc;
    }
  }

  &-agree {
    width: 100%;
    @include flex-box(flex-start, center);
  }

  .have-account {
    width: 100%;
    margin-top: 10px;
    color: #341b1b;
    font-size: 14px;
    font-family: $font-weight-5;
    @include flex-box(flex-end, center);
  }

  .input-icon {
    margin-right: 10px;
  }

  .login-btn {
    margin-top: 24px;
  }

  .password-tip {
    position: absolute;
    font-size: 12px;
    color: #666666;
  }
}

::v-deep(.el-input__prefix) {
  line-height: 46px;
}

::v-deep(.el-select .el-input) {
  width: 110px;
}

::v-deep(.el-input-group__prepend) {
  background-color: #fff;
}
</style>
